import { Injectable, signal, effect } from '@angular/core';

export type ThemeMode = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  // Signal for reactive theme state
  themeMode = signal<ThemeMode>('light');

  constructor() {
    // Load saved theme on initialization
    this.loadTheme();

    // Apply theme whenever it changes
    effect(() => {
      this.applyTheme();
    });
  }

  /**
   * Toggle between light and dark mode
   */
  toggleDarkMode(): void {
    const newMode = this.themeMode() === 'light' ? 'dark' : 'light';
    this.themeMode.set(newMode);
    this.saveTheme();
  }

  /**
   * Set theme mode explicitly
   */
  setThemeMode(mode: ThemeMode): void {
    this.themeMode.set(mode);
    this.saveTheme();
  }

  /**
   * Apply theme to document
   */
  private applyTheme(): void {
    const mode = this.themeMode();

    // Apply dark mode class
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  /**
   * Save theme to localStorage
   */
  private saveTheme(): void {
    localStorage.setItem('theme-mode', this.themeMode());
  }

  /**
   * Load theme from localStorage
   */
  private loadTheme(): void {
    try {
      const savedMode = localStorage.getItem('theme-mode') as ThemeMode | null;

      if (savedMode && (savedMode === 'light' || savedMode === 'dark')) {
        this.themeMode.set(savedMode);
      } else {
        // Default to light mode if no saved preference
        this.themeMode.set('light');
      }
    } catch (error) {
      console.error('Error loading theme:', error);
      // Fallback to light mode
      this.themeMode.set('light');
    }
  }
}
